import { t } from "../i18n/index.js";
/** Donaciones — ajeno al juego. Edita URLs y copy en DONATION_CONFIG. */
export interface DonationLink {
  label: string;
  href: string;
  /** Texto corto bajo el botón (opcional). */
  hint?: string;
}

export const DONATION_CONFIG = {
  enabled: true,
  /** Enlace principal del botón dorado (Ko-fi recomendado). */
  primaryHref: "",
  cta: t("☕ INVÍTAME A UN CAFÉ"),
  ctaHint: "10 segundos · cualquier cantidad · cero compromiso",
  badge: t("Indie · Sin ads · Sin paywall"),
  hook: t("Un segundo, antes del pitido inicial…"),
  headline: t("¿Te lo has pasado bien? No dejes que FYNTA se quede en el banquillo."),
  subline:
    t("Proyecto hecho por una sola persona, sin inversores ni publicidad. Si disfrutaste del partido, un café mantiene el juego gratis para todos — y financia lo que viene."),
  socialProof: t("Cada apoyo suma. Tú eliges si esto sigue creciendo."),
  /**
   * Version corta para la franja del pie. El menu es para jugar: la peticion
   * vive abajo, en una linea, sin competir en tamaño con las opciones de
   * partido. Los textos largos de arriba se conservan por si se quiere volver
   * a una tarjeta grande en otra pantalla (fin de partido, por ejemplo).
   */
  slimline: t("Hecho por una persona, sin ads ni paywall. Un café lo mantiene gratis."),
  links: [
    { label: "Ko-fi", href: "", hint: "Más popular" },
    { label: "PayPal", href: "" },
    { label: "Buy Me a Coffee", href: "" },
  ] satisfies DonationLink[],
} as const;

function activeLinks(): DonationLink[] {
  return DONATION_CONFIG.links.filter((link) => link.href.trim().length > 0);
}

function primaryUrl(): string {
  const direct = DONATION_CONFIG.primaryHref.trim();
  if (direct) return direct;
  return activeLinks()[0]?.href ?? "";
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function renderDonationFooter(): string {
  if (!DONATION_CONFIG.enabled) return "";

  const c = DONATION_CONFIG;
  const url = primaryUrl();
  const links = activeLinks();

  /*
   * Sin enlace no se pinta boton. Antes salia una version `is-pending`: un
   * boton dorado, grande y con pulso que no llevaba a ninguna parte. Quien lo
   * tocaba no obtenia nada, y en una pantalla de menu eso se lee como un pago
   * roto, no como un aviso para el que programa. La franja se queda en una
   * linea de texto hasta que DONATION_CONFIG tenga una URL de verdad.
   */
  const ctaBlock = url
    ? `<a class="hub-donate-cta" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
        <span class="hub-donate-cta-text">${escapeHtml(c.cta)}</span>
        <span class="hub-donate-cta-shine" aria-hidden="true"></span>
      </a>`
    : "";

  const altLinks =
    links.length > 0
      ? `<nav class="hub-donate-alt" aria-label="${t("Otras formas de apoyar")}">
          <div class="hub-donate-alt-row">
            ${links
              .map(
                (link) =>
                  `<a class="hub-donate-alt-link" href="${escapeHtml(link.href)}" target="_blank" rel="noopener noreferrer">
                    ${escapeHtml(link.label)}
                  </a>`,
              )
              .join("")}
          </div>
        </nav>`
      : "";

  return `
    <aside class="hub-donate hub-donate--slim" aria-label="${t("Apoyar FYNTA")}">
      <p class="hub-donate-line">
        <span class="hub-donate-badge">${escapeHtml(c.badge)}</span>
        <span class="hub-donate-slim">${escapeHtml(c.slimline)}</span>
      </p>
      ${ctaBlock}
      ${altLinks}
    </aside>
  `.trim();
}
